import type { Option } from '../../components/search-autocomplete/types';

export type SearchType = 'Suburb' | 'Agent' | 'Agency';

export interface SearchProperties {
  searchType: SearchType;
  suburbSearched: string[];
  agentSearched: string;
  agentName: string;
  agentId: number | '';
  agencySearched: string;
  agencyName: string;
  agencyId: number | '';
  state: string;
  suburb: string;
  postcode: string;
}

const SUBURB_SLUG = /^(.+)-([a-z]{2,3})-(\d{4})$/i;

const slugPartsOf = ({ category, value }: Option): string[] =>
  category === 'Suburb' ? (value?.match(SUBURB_SLUG) ?? []) : [];

export const suburbNameOf = (option: Option): string => {
  if (option.suburbName) {
    return option.suburbName;
  }

  if (option.category !== 'Suburb') {
    return '';
  }

  return option.label?.split(',')[0]?.trim() ?? '';
};

export const stateOf = (option: Option): string =>
  option.state ?? slugPartsOf(option)[2]?.toUpperCase() ?? '';

export const postcodeOf = (option: Option): string =>
  option.postcode ?? slugPartsOf(option)[3] ?? '';

const buildSearchProperties = (
  options: Option[],
): SearchProperties | undefined => {
  if (options.length === 0) {
    return undefined;
  }

  const [firstOption] = options;
  const searchType = firstOption.category;
  const isAgent = searchType === 'Agent';
  const isAgency = searchType === 'Agency';

  const agentName = isAgent ? firstOption.label : '';
  const agencyName = isAgency
    ? (firstOption.agencyName ?? firstOption.label)
    : (isAgent && firstOption.agencyName) || '';
  const suburb = suburbNameOf(firstOption);

  return {
    searchType,
    suburbSearched: options
      .filter(({ category }) => category === 'Suburb')
      .map(({ value }) => value),
    agentSearched:
      isAgent && agencyName ? `${agentName} | ${agencyName}` : agentName,
    agentName: isAgent && suburb ? `${agentName} - ${suburb}` : agentName,
    agentId: (isAgent && firstOption.contactId) || '',
    agencySearched: agencyName,
    agencyName,
    agencyId: firstOption.agencyId || '',
    state: stateOf(firstOption),
    suburb,
    postcode: postcodeOf(firstOption),
  };
};

export default buildSearchProperties;
